'use client';

import { useState } from 'react';
import { useInsufficientCreditsToast } from '@/new_components/alerts/use-insufficient-credits-toast';
import { Bell, Bookmark, BookmarkCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useSession } from '@/hooks/use-session';
import { useCompanyWatch } from '@/hooks/use-company-watch';
import { useWatchlist } from '@/hooks/use-watchlist';
import { Button } from '@/components/ui/button';
import { ListWatchWizard } from '@/new_components/lists/ListWatchWizard';

/**
 * The bookmark, plus — once the company is watched — a bell that opens the
 * alert configuration.
 *
 * Two controls rather than one because they answer different questions.
 * Pressing the bookmark is the one-click case and subscribes to the three
 * document events, exactly as it always has; the bell is where the other
 * fourteen criteria, the recipients and the cadence live. The wizard is the
 * *same* one a watched list uses — since the watch layer merged them, a
 * company and a list are one kind of subscription and a second picker would
 * be a second vocabulary to drift.
 *
 * The bell only appears once watched: configuring an unwatched company would
 * start watching it, and two buttons that both mean "start watching" is a
 * worse first impression than one.
 */
export function WatchlistButton({
  euId,
  className,
}: {
  euId: string;
  className?: string;
}) {
  const { data: session } = useSession();
  const { t } = useTranslation();
  const { isWatched, toggle, isMutating } = useWatchlist(euId);
  const { watch, save, remove, isBusy } = useCompanyWatch(euId);
  const showInsufficient = useInsufficientCreditsToast();
  const [wizardOpen, setWizardOpen] = useState(false);

  if (!session) return null;

  const label = isWatched
    ? t('watchlist.remove', 'Remove from Watchlist')
    : t('watchlist.add', 'Add to Watchlist');

  const handleClick = async () => {
    const action = await toggle();
    if (action === 'added') {
      toast.success(t('watchlist.added', 'Added to Watchlist'), {
        position: 'top-center',
      });
    } else if (action === 'removed') {
      toast.success(t('watchlist.removed', 'Removed from Watchlist'), {
        position: 'top-center',
      });
    } else if (action === 'credits') {
      showInsufficient();
    }
  };

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        onClick={handleClick}
        disabled={isMutating}
        className={cn(
          'h-10 w-10 text-foreground hover:text-foreground',
          className,
        )}
        aria-pressed={isWatched}
        aria-label={label}
        title={label}
      >
        {isWatched ? (
          <BookmarkCheck className="size-6 fill-current text-foreground" />
        ) : (
          <Bookmark className="size-6 text-foreground" />
        )}
      </Button>

      {isWatched && (
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setWizardOpen(true)}
          className="h-10 w-10 text-muted-foreground hover:text-foreground"
          aria-label={t('watchlist.configureAlerts', 'Configure alerts')}
          title={t('watchlist.configureAlerts', 'Configure alerts')}
        >
          <Bell className="size-5" />
        </Button>
      )}

      <ListWatchWizard
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        watch={watch}
        // A company has no segments, which is what hides Segment Change.
        segmentConfig={null}
        subjectKind="company"
        euId={euId}
        onSave={async (config) => {
          // A 402 throws InsufficientCreditsError; the wizard shows the
          // top-up prompt and stays open.
          await save(config);
          toast.success(t('watchlist.alertsSaved', 'Alerts updated'), {
            position: 'top-center',
          });
        }}
        onDisable={async () => {
          await remove();
          toast.success(t('watchlist.removed', 'Removed from Watchlist'), {
            position: 'top-center',
          });
        }}
        isBusy={isBusy}
      />
    </>
  );
}
